import { urlFor } from "@/utils/client";
import { constants, getYouTubeId } from "@/utils/constants";
import { PortableText } from "@portabletext/react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { Media } from "./media";
import { Partners } from "./partners";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <div className="aspect-video w-full bg-gray-200" />,
});

export const portableTextComponents = {
  block: {
    normal: ({ children }: any) => <p>{children}</p>,
    h1: ({ children }: any) => (
      <h1 className="font-display text-4xl lg:text-5xl text-balance max-w-4xl">{children}</h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-balance">{children}</h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="font-display text-xl md:text-2xl lg:text-3xl text-balance">{children}</h3>
    ),
    h4: ({ children }: any) => (
      <h4 className="font-display text-xl md:text-2xl text-balance">{children}</h4>
    ),
    h5: ({ children }: any) => (
      <h5 className="font-display text-base md:text-lg lg:text-xl text-balance">{children}</h5>
    ),
    h6: ({ children }: any) => (
      <h6 className="font-display text-sm md:text-base lg:text-lg text-balance">{children}</h6>
    ),
  },
  list: {
    bullet: ({ children }: any) => <ul className="list-disc pl-6">{children}</ul>,
    number: ({ children }: any) => <ol className="list-decimal pl-6">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }: any) => <li>{children}</li>,
    number: ({ children }: any) => <li>{children}</li>,
  },
  types: {
    iwc: ({ value }: { value: any }) => {
      if (!value?.image) return null;
      return (
        <Image
          width={constants.imageWidth}
          height={constants.imageHeight}
          alt={value.image?.caption ?? ""}
          className=" object-cover"
          src={urlFor(value.image).url()}
        />
      );
    },
    vwc: ({ value }: { value: any }) => {
      if (!value?.playbackId) return null;
      return (
        <MuxPlayer
          streamType="on-demand"
          playbackId={value.playbackId}
          thumbnailTime={0.1}
          disableCookies
          className="w-full h-full aspect-video object-cover"
        />
      );
    },
    ywc: ({ value }: { value: any }) => {
      if (!value?.url) return null;
      const videoId = getYouTubeId(value.url);
      if (!videoId) return null;
      const ytParams = value.autoplay
        ? `?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&modestbranding=1&rel=0&showinfo=0`
        : `?rel=0`;
      return (
        <div className="aspect-video relative w-full">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}${ytParams}`}
            title={value.caption ?? "YouTube video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        </div>
      );
    },
    partners: ({ value }: { value: any }) => (
      <div className="w-full">
        <Partners {...value} />
      </div>
    ),
    media: ({ value }: { value: any }) => (
      <div className="w-full">
        <Media {...value} />
      </div>
    ),
  },
  marks: {
    // @ts-ignore
    link: ({ children, value }: { children: any; value?: any }) => {
      const href = value?.href ?? value?.url;
      if (!href) return <>{children}</>;
      const linkClass =
        "text-darkBlue underline decoration-from-font underline-offset-[0.2em] transition-colors hover:text-darkGrey";
      if (href.includes("http") || href.includes("tel:") || href.includes("mailto:")) {
        return (
          <a href={href} target="_blank" rel="noreferrer" className={linkClass}>
            {children}
          </a>
        );
      }
      return (
        <Link href={href} className={linkClass}>
          {children}
        </Link>
      );
    },
    textColor: ({ children, value }: any) => <span style={{ color: value.value }}>{children}</span>,
    highlightColor: ({ children, value }: any) => (
      <span style={{ background: value.value }}>{children}</span>
    ),
  },
};

export function Pt(props: { data: any }) {
  if (!props.data) return null;
  return (
    <article className="section-y">
      <div className="rt">
        <PortableText value={props.data} components={portableTextComponents} />
      </div>
    </article>
  );
}
